function isPresent (value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function normalizeDeviceSn (value) {
  return String(value || '').trim().toUpperCase()
}

function normalizeKey (value) {
  return String(value).toLowerCase().replace(/[\s_\-/.()%]+/g, '')
}

function collectValues (value, result = new Map()) {
  if (typeof value === 'string') {
    try {
      return collectValues(JSON.parse(value), result)
    } catch (error) {
      return result
    }
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectValues(item, result))
    return result
  }
  if (!value || typeof value !== 'object') return result
  Object.keys(value).forEach(key => {
    const item = value[key]
    if (item && typeof item === 'object') collectValues(item, result)
    else if (!result.has(normalizeKey(key))) result.set(normalizeKey(key), item)
    if (typeof item === 'string' && /^[{[]/.test(item.trim())) collectValues(item, result)
  })
  return result
}

function findValue (values, aliases) {
  for (const alias of aliases) {
    const value = values.get(normalizeKey(alias))
    if (isPresent(value)) return value
  }
  return undefined
}

function numberValue (value) {
  if (!isPresent(value)) return null
  const number = Number.parseFloat(String(value))
  return Number.isFinite(number) ? number : null
}

function formatDuration (value) {
  const hours = numberValue(value)
  if (hours === null) return '--'
  const totalMinutes = Math.round(hours * 60)
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}min`
}

function formatMinutes (value) {
  const minutes = numberValue(value)
  return minutes === null ? '--' : `${Math.round(minutes)}min`
}

function formatPercent (value) {
  const number = numberValue(value)
  return number === null ? '--' : `${Number.parseFloat(number.toFixed(2))}%`
}

function parseDateValue (value) {
  if (!isPresent(value)) return null
  const text = String(value).trim()
  if (/^\d{14}$/.test(text)) {
    return new Date(
      Number(text.slice(0, 4)), Number(text.slice(4, 6)) - 1, Number(text.slice(6, 8)),
      Number(text.slice(8, 10)), Number(text.slice(10, 12)), Number(text.slice(12, 14))
    )
  }
  const parsed = new Date(text.replace(' ', 'T'))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function deriveLatency (values) {
  const bed = parseDateValue(findValue(values, ['bedtime', 'bedTime', 'inBedTime', 'goToBedTime']))
  const sleep = parseDateValue(findValue(values, ['sleep_time', 'sleepTime', 'fallAsleepTime', 'sleepStartTime']))
  if (!bed || !sleep || sleep < bed) return undefined
  return Math.round((sleep.getTime() - bed.getTime()) / 60000)
}

export function getHistoryTotal (total) {
  const number = Number(total)
  return Number.isFinite(number) ? Math.max(number - 1, 0) : 0
}

export function canRequestAccountReports (session) {
  return Boolean(session && String(session.token || '').trim())
}

export function buildAccountReportDetailQuery (reportId) {
  return { reportId }
}

export function resolveReportDeviceSn (routeSn, accountSession = {}) {
  return normalizeDeviceSn(routeSn) ||
    normalizeDeviceSn(accountSession.deviceSn) ||
    normalizeDeviceSn(accountSession.deviceList && accountSession.deviceList[0] && accountSession.deviceList[0].sn)
}

export function splitLatestReport (page = {}) {
  const list = Array.isArray(page.list) ? page.list : []
  return {
    latest: list[0] || null,
    history: list.slice(1),
    historyTotal: getHistoryTotal(page.total)
  }
}

export function appendHistory (state, page = {}) {
  return {
    ...state,
    history: (state.history || []).concat(Array.isArray(page.list) ? page.list : [])
  }
}

export function formatHomeReportDate (value) {
  const text = String(value || '')
  if (!/^\d{8}/.test(text)) return '--'
  return `${text.slice(0, 4)}年${Number(text.slice(4, 6))}月${Number(text.slice(6, 8))}日`
}

export function getGreeting (date = new Date(), name) {
  const hour = date.getHours()
  const greeting = hour >= 5 && hour < 12 ? '早上好' : hour >= 12 && hour < 18 ? '下午好' : '晚上好'
  const normalizedName = String(name || '').trim()
  return normalizedName ? `${greeting}，${normalizedName}` : greeting
}

export function buildHomeMetrics (detail = {}) {
  const values = collectValues(detail)
  const duration = findValue(values, ['total_sleep_time', 'totalSleepTime', 'sleepDuration'])
  const latency = findValue(values, ['sleep_latency', 'sleepLatency']) ?? deriveLatency(values)
  const awakeCount = findValue(values, ['awakeCount'])
  const efficiency = findValue(values, ['sleepEfficiency'])
  return {
    duration: formatDuration(duration),
    latency: formatMinutes(latency),
    awakeCount: isPresent(awakeCount) ? `${awakeCount}次` : '--',
    efficiency: formatPercent(efficiency)
  }
}

const stageKeys = {
  深睡眠期: 'deep',
  中睡眠期: 'moderate',
  浅睡眠期: 'light',
  清醒: 'awake'
}

export function buildSleepStageTimeline (series) {
  if (!series || !Array.isArray(series.stages) || !series.stages.length) return null
  const segments = []
  series.stages.forEach(stageName => {
    const stage = stageKeys[stageName]
    if (!stage) return
    const current = segments[segments.length - 1]
    if (current && current.stage === stage) current.count += 1
    else segments.push({ stage, count: 1 })
  })
  if (!segments.length) return null
  const total = segments.reduce((sum, item) => sum + item.count, 0)
  return {
    start: series.labels && series.labels[0] ? series.labels[0] : '--',
    end: series.labels && series.labels[series.labels.length - 1] ? series.labels[series.labels.length - 1] : '--',
    segments: segments.map(item => ({
      stage: item.stage,
      percent: Number.parseFloat((item.count / total * 100).toFixed(2))
    }))
  }
}
